import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import "./AlertFormEdit.css";
import { useIntl } from "react-intl";
import { Toast } from "../../../helpers/alert";
import useFilteredUsersByDevice from "../../../hooks/useFilteredUsersByDevice";

const DataAbsenceAlertFormEdit = ({
  devices,
  users,
  onCancel,
  onSave,
  initialData,
  userPrivilege,
}) => {
  const intl = useIntl();

  const notifyOptions = useMemo(
    () => [
      { value: "1d", label: "día" },
      { value: "1w", label: "semana" },
    ],
    []
  );

  const getInitialNotifyEvery = useCallback(() => {
    const frequency = initialData?.config?.frequency || {};

    if (frequency?.daily?.enabled) {
      return notifyOptions[0];
    }

    if (frequency?.weekly?.enabled) {
      return notifyOptions[1];
    }

    const legacyRateLimit = String(frequency?.realtime?.rateLimit || "").toLowerCase();

    if (legacyRateLimit === "1w") {
      return notifyOptions[1];
    }

    return notifyOptions[0];
  }, [initialData, notifyOptions]);

  const getInitialRecipients = useCallback(() => {
    const frequency = initialData?.config?.frequency || {};
    const daily = frequency?.daily || {};
    const weekly = frequency?.weekly || {};
    const realtime = frequency?.realtime || {};

    const scheduledBlock = daily?.enabled
      ? daily
      : weekly?.enabled
      ? weekly
      : null;

    return {
      emails:
        (Array.isArray(scheduledBlock?.emails) && scheduledBlock.emails.length > 0
          ? scheduledBlock.emails
          : realtime?.emails) || [],
      phones:
        (Array.isArray(scheduledBlock?.phones) && scheduledBlock.phones.length > 0
          ? scheduledBlock.phones
          : realtime?.phones) || [],
    };
  }, [initialData]);

  const toEmailOptions = useCallback(
    (values = []) => {
      const byId = new Map((users || []).map((u) => [Number(u.id), u]));
      const byEmail = new Map(
        (users || []).map((u) => [String(u.email || "").toLowerCase(), u])
      );

      return values
        .map((v) => {
          const str = String(v ?? "");
          const isNumeric = typeof v === "number" || /^[0-9]+$/.test(str);

          if (isNumeric) {
            const u = byId.get(Number(v));
            return u?.email ? { value: u.id, label: u.email } : null;
          }

          const email = str.trim().toLowerCase();
          const u = byEmail.get(email);
          if (u) return { value: u.id, label: u.email };
          return email ? { value: email, label: String(v) } : null;
        })
        .filter(Boolean);
    },
    [users]
  );

  const toPhoneOptions = useCallback(
    (values = []) => {
      const byId = new Map((users || []).map((u) => [Number(u.id), u]));
      const byPhone = new Map((users || []).map((u) => [String(u.phone), u]));

      return values
        .map((v) => {
          const str = String(v ?? "");
          const isNumeric = typeof v === "number" || /^[0-9]+$/.test(str);

          if (isNumeric) {
            const u = byId.get(Number(v));
            return u?.phone ? { value: u.id, label: String(u.phone) } : null;
          }

          const u = byPhone.get(str);
          if (u) return { value: u.id, label: String(u.phone) };
          return str ? { value: str, label: str } : null;
        })
        .filter(Boolean);
    },
    [users]
  );

  const toDeviceOptions = useCallback(
    (deviceIds = []) => {
      const byId = new Map((devices || []).map((d) => [d.value, d]));

      return deviceIds.map((d) => {
        const id = d?.device_id ?? d?.id ?? d;

        return (
          byId.get(id) || {
            value: id,
            label: d?.device?.name
              ? `${d.device.name} (${d.device.serial_number})`
              : String(id),
          }
        );
      });
    },
    [devices]
  );

  const buildStateFromInitial = useCallback(() => {
    if (!initialData) return null;

    const notifyEvery = getInitialNotifyEvery();
    const recipients = getInitialRecipients();

    return {
      name: initialData?.name || "",
      hours: Number(initialData?.config?.hoursNoData ?? 24),
      active: initialData?.active === 1 || initialData?.active === true,
      devices: toDeviceOptions(initialData?.config?.devices || []),
      notifyEvery,
      emails: toEmailOptions(recipients.emails || []),
      phones: toPhoneOptions(recipients.phones || []),
    };
  }, [
    initialData,
    getInitialNotifyEvery,
    getInitialRecipients,
    toDeviceOptions,
    toEmailOptions,
    toPhoneOptions,
  ]);

  const [formData, setFormData] = useState(() => {
    return (
      buildStateFromInitial() || {
        name: "",
        hours: 24,
        active: true,
        devices: [],
        notifyEvery: notifyOptions[0],
        emails: [],
        phones: [],
      }
    );
  });

  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);

  const lastInitKeyRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);

  const initKey = useMemo(
    () =>
      initialData?.id ??
      initialData?.uuid ??
      initialData?.name ??
      JSON.stringify({
        name: initialData?.name,
        active: initialData?.active,
        hoursNoData: initialData?.config?.hoursNoData,
        devices: (initialData?.config?.devices || []).map(
          (d) => d?.device_id ?? d?.id ?? d
        ),
        frequency: initialData?.config?.frequency,
      }),
    [initialData]
  );

  useEffect(() => {
    if (!initialData || !users?.length || !devices?.length) return;

    const sameInit = lastInitKeyRef.current === initKey;
    if (isDirty && sameInit) return;

    const next = buildStateFromInitial();
    if (!next) return;

    setFormData((prev) => {
      const simplify = (s) => ({
        name: s.name,
        active: !!s.active,
        hours: Number(s.hours),
        deviceIds: (s.devices || []).map((d) => d.value).sort(),
        notifyEvery: s.notifyEvery?.value || s.notifyEvery,
        emailVals: (s.emails || []).map((e) => e.value).sort(),
        phoneVals: (s.phones || []).map((p) => p.value).sort(),
      });

      return JSON.stringify(simplify(prev)) === JSON.stringify(simplify(next))
        ? prev
        : next;
    });

    lastInitKeyRef.current = initKey;
    setIsDirty(false);
  }, [initKey, initialData, users, devices, buildStateFromInitial, isDirty]);

  const filteredUsers = useFilteredUsersByDevice(
    formData.devices,
    users,
    userPrivilege
  );

  const emailOptions = useMemo(
    () =>
      (filteredUsers || [])
        .filter((u) => !!u?.email)
        .map((u) => ({ value: u.id, label: u.email })),
    [filteredUsers]
  );

  const phoneOptions = useMemo(
    () =>
      (filteredUsers || [])
        .filter((u) => !!u?.phone)
        .map((u) => ({ value: u.id, label: String(u.phone) })),
    [filteredUsers]
  );

  const handleChange = (field, value) => {
    setIsDirty(true);

    if (field === "hours") {
      setFormData((prev) => ({ ...prev, hours: Number(value) }));
      return;
    }

    setFormData((prev) => {
      if (field === "devices") {
        return {
          ...prev,
          emails: [],
          phones: [],
          devices: value || [],
        };
      }

      return { ...prev, [field]: value };
    });
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "El nombre es obligatorio";
    if (!formData.devices?.length)
      newErrors.devices = "Selecciona al menos un dispositivo";
    if (!formData.hours || Number(formData.hours) <= 0)
      newErrors.hours = "Debe ingresar un valor mayor a 0";

    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
  }, [formData]);

  useEffect(() => {
    validateForm();
  }, [formData, validateForm]);

  const buildScheduledFrequency = () => {
    const selectedFrequency = formData.notifyEvery?.value || "1d";
    const selectedEmails = formData.emails.map((e) => e.value);
    const selectedPhones = formData.phones.map((p) => p.value);

    return {
      daily: {
        enabled: selectedFrequency === "1d",
        time: selectedFrequency === "1d" ? "09:00" : null,
        emails: selectedFrequency === "1d" ? selectedEmails : [],
        phones: selectedFrequency === "1d" ? selectedPhones : [],
      },
      weekly: {
        enabled: selectedFrequency === "1w",
        day: selectedFrequency === "1w" ? "monday" : null,
        time: selectedFrequency === "1w" ? "09:00" : null,
        emails: selectedFrequency === "1w" ? selectedEmails : [],
        phones: selectedFrequency === "1w" ? selectedPhones : [],
      },
      monthly: {
        enabled: false,
        day: null,
        time: null,
        emails: [],
        phones: [],
      },
    };
  };

  const handleSubmit = () => {
    Swal.fire({
      title: "¿Desea guardar los cambios de la alerta?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.formatMessage({
        id: "confirm.yes",
        defaultMessage: "Sí, continuar.",
      }),
      cancelButtonText: intl.formatMessage({
        id: "confirm.no",
        defaultMessage: "Cancelar",
      }),
    }).then((result) => {
      if (result.isConfirmed && isValid) {
        const selectedEmails = formData.emails.map((e) => e.value);
        const selectedPhones = formData.phones.map((p) => p.value);
        const scheduledFrequency = buildScheduledFrequency();

        const payload = {
          type: "AUSENCIA",
          name: formData.name?.toUpperCase(),
          active: formData.active ? 1 : 0,
          config: {
            devices: formData.devices.map((d) => d.value),
            hoursNoData: formData.hours,
            frequency: {
              realtime: {
                enabled:
                  (formData.emails?.length || 0) > 0 ||
                  (formData.phones?.length || 0) > 0,
                rateLimit: null,
                emails: selectedEmails,
                phones: selectedPhones,
              },
              daily: scheduledFrequency.daily,
              weekly: scheduledFrequency.weekly,
              monthly: scheduledFrequency.monthly,
            },
          },
        };

        onSave(payload);
      } else if (!isValid) {
        Toast.fire({
          icon: "info",
          title: intl.formatMessage(
            {
              id: "error.saveAlert",
              defaultMessage: "No se pudo guardar la alerta: {errors}",
            },
            { errors: Object.values(errors).join(", ") }
          ),
        });
      }
    });
  };

  return (
    <div className="ausencia-form">
      <h3 className="ausencia-title">Editar alerta: Ausencia de datos</h3>

      <label className="ausencia-label">Nombre de la alerta</label>
      <input
        type="text"
        className="ausencia-input"
        value={formData.name}
        onChange={(e) => handleChange("name", e.target.value)}
        placeholder="Ej: Alerta de comunicación"
      />
      {errors.name && <small className="error-text">{errors.name}</small>}

      <label className="ausencia-label">Condición</label>
      <div className="ausencia-inline">
        Cuando haga más de
        <input
          type="number"
          className="ausencia-input small"
          min="1"
          value={formData.hours}
          onChange={(e) => handleChange("hours", e.target.value)}
        />
        horas que no se reciben datos
      </div>
      {errors.hours && <small className="error-text">{errors.hours}</small>}

      <label className="ausencia-label">¿Activar?</label>
      <div className="ausencia-inline">
        <label>
          <input
            type="radio"
            checked={!!formData.active}
            onChange={() => handleChange("active", true)}
          />{" "}
          Sí
        </label>
        <label>
          <input
            type="radio"
            checked={!formData.active}
            onChange={() => handleChange("active", false)}
          />{" "}
          No
        </label>
      </div>

      <label className="ausencia-label">Aplica a</label>
      <Select
        isMulti
        className="ausencia-select"
        options={devices}
        value={formData.devices}
        onChange={(val) => handleChange("devices", val)}
        placeholder="Selecciona uno o más dispositivos"
      />
      {errors.devices && <small className="error-text">{errors.devices}</small>}

      <label className="ausencia-label">Avisos</label>
      <div className="ausencia-inline">
        Enviar recordatorio programado cada
        <Select
          className="ausencia-select small"
          options={notifyOptions}
          value={formData.notifyEvery}
          onChange={(val) => handleChange("notifyEvery", val)}
        />
        a las 09:00 hs
      </div>

      <div className="ausencia-inline">
        <label>
          <input
            type="checkbox"
            checked={formData.emails.length > 0}
            readOnly
          />{" "}
          Enviar aviso inicial, recuperación y recordatorios por correo electrónico
        </label>
      </div>

      <Select
        isMulti
        className="ausencia-select"
        options={emailOptions}
        value={formData.emails}
        onChange={(val) => handleChange("emails", val || [])}
        isDisabled={!formData.devices?.length}
        placeholder="Selecciona usuarios"
      />

      {/* SMS deshabilitado por ahora */}
      {/*
      <div className="ausencia-inline">
        <label>
          <input
            type="checkbox"
            checked={formData.phones.length > 0}
            readOnly
          />{" "}
          Enviar un aviso por SMS
        </label>
      </div>

      <Select
        isMulti
        className="ausencia-select"
        options={phoneOptions}
        value={formData.phones}
        onChange={(val) => handleChange("phones", val || [])}
        isDisabled={!formData.devices?.length}
        placeholder="Selecciona usuarios"
      />
      */}

      <div className="ausencia-actions">
        <button
          className="ausencia-btn-save"
          onClick={handleSubmit}
          disabled={!isValid}
        >
          Guardar
        </button>
        <button className="ausencia-btn-cancel" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default DataAbsenceAlertFormEdit;
